import React, { useState, useEffect, useRef } from "react";
import InsertLinkIcon from "@mui/icons-material/InsertLink";
import upload_icon from "../../images/managedata/upload.svg";
import doenloadsam_icon from "../../images/managedata/download_sam.svg";
import download_icon from "../../images/managedata/download.svg";

import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  MenuItem,
  useMediaQuery,
  useTheme,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import axios from "axios";
import config from "../../config.js";
export default function ImportExport({ school = false } = {}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();

  // State for section selection, file, and feedback
  const [importSection, setImportSection] = useState("");
  const [exportSection, setExportSection] = useState("");
  const [file, setFile] = useState(null);
  const [importSectionError, setImportSectionError] = useState("");
  const [importFileError, setImportFileError] = useState("");
  const [importStatus, setImportStatus] = useState({
    message: "",
    severity: "info",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [importSections, setImportSections] = useState([]);
  const [exportSections, setExportSections] = useState([]);
  const [organizationId, setOrganizationId] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const formatUploadErrors = (errors = []) => {
    if (!Array.isArray(errors) || errors.length === 0) {
      return "";
    }

    return errors
      .map((errorItem) => {
        const rowPrefix = errorItem?.row ? `Row ${errorItem.row}: ` : "";
        const detail =
          errorItem?.error || errorItem?.message || errorItem?.reason || "";

        return detail ? `${rowPrefix}${detail}` : "";
      })
      .filter(Boolean)
      .join(" | ");
  };

  // Load user data and permissions from localStorage
  useEffect(() => {
    try {
      const userData = JSON.parse(localStorage.getItem("userState") || "{}");
      const { user, features = [] } = userData;

      if (user?.organizationId) {
        setOrganizationId(user.organizationId);
      } else {
        setSnackbar({
          open: true,
          message: t("noOrganizationId"),
          severity: "error",
        });
      }

      // Define permission mappings for import and export
      const importPermissions = [
        {
          section: "driver",
          label: "Driver",
          api_paths: ["/api/driver/add", "/api/driver/create"],
          action: "POST",
        },
        {
          section: "user",
          label: "User",
          api_paths: ["/api/user"],
          action: "POST",
        },
        {
          section: "vehicle",
          label: "Vehicle",
          api_paths: ["/api/vehicle"],
          action: "POST",
        },
        {
          section: "stop",
          label: "Stop",
          api_paths: ["/api/stop"],
          action: "POST",
        },
        {
          section: "route",
          label: "Route",
          api_paths: ["/api/route"],
          action: "POST",
        },
        {
          section: "site",
          label: "Site",
          api_paths: ["/api/site"],
          action: "POST",
        },
        {
          section: "student",
          label: "Student",
          api_paths: ["/api/student"],
          action: "POST",
        },
        {
          section: "employee",
          label: "Attendant",
          api_paths: ["/api/employee"],
          action: "POST",
        },
      ];

      const exportPermissions = [
        {
          section: "driver",
          api_paths: ["/api/driver/organization"],
          action: "GET",
        },
        { section: "user", api_paths: ["/api/user"], action: "GET" },
        {
          section: "vehicle",
          api_paths: ["/api/vehicle/organization"],
          action: "GET",
        },
        {
          section: "stop",
          api_paths: ["/api/stop/organization"],
          action: "GET",
        },
        {
          section: "route",
          api_paths: ["/api/route/organization"],
          action: "GET",
        },
        {
          section: "site",
          api_paths: ["/api/site/organization"],
          action: "GET",
        },
        {
          section: "Student",
          api_paths: ["/api/vehicle/organization"],
          action: "GET",
        },
        {
          section: "Attendant",
          api_paths: ["/api/vehicle/organization"],
          action: "GET",
        },
      ];

      const hasPermission = (perm) =>
        features.some(
          (feature) =>
            perm.api_paths.includes(feature.api_path) &&
            feature.action === perm.action,
        );

      const schoolImportSections = [
        "student",
        "employee",
        "driver",
        "vehicle",
        "stop",
        "route",
      ];

      const standardImportSections = [
        "driver",
        "user",
        "vehicle",
        "stop",
        "route",
        "site",
      ];

      const visibleImportSections = importPermissions.filter((perm) =>
        school
          ? schoolImportSections.includes(perm.section)
          : standardImportSections.includes(perm.section),
      );

      const allowedImportSections = visibleImportSections
        .filter((perm) => {
          if (!school) {
            return hasPermission(perm);
          }

          if (perm.section === "student" || perm.section === "employee") {
            return true;
          }

          return hasPermission(perm);
        })
        .map((perm) => ({
          value: perm.section,
          label: t(perm.label),
        }));

      const allowedExportSections = exportPermissions
        .filter((perm) =>
          features.some(
            (feature) =>
              perm.api_paths.includes(feature.api_path) &&
              feature.action === perm.action,
          ),
        )
        .map((perm) => ({
          value: perm.section,
          label: t(perm.section),
        }));

      setImportSections(allowedImportSections);
      setExportSections(allowedExportSections);
    } catch (error) {
      console.error("Error parsing userState:", error);
      setSnackbar({
        open: true,
        message: t("invalidUserData"),
        severity: "error",
      });
    }
  }, [school, t]);

  // Handle section change
  const handleImportSectionChange = (event) => {
    setImportSection(event.target.value);
    setImportSectionError("");
    setImportFileError("");
    setImportStatus({ message: "", severity: "info" });
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null; // Reset file input
    }
  };

  const handleExportSectionChange = (event) => {
    setExportSection(event.target.value);
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    setImportFileError("");
    setImportStatus({ message: "", severity: "info" });
    if (
      selectedFile &&
      selectedFile.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ) {
      setFile(selectedFile);
    } else {
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
      setImportFileError("Please upload a valid .xlsx file.");
    }
  };

  // Handle file upload
  const handleUpload = async () => {
    setImportStatus({ message: "", severity: "info" });
    if (!importSection) {
      setImportSectionError("Section is required for upload.");
      return;
    }
    setImportSectionError("");
    if (!file) {
      setImportFileError("File is required for upload.");
      return;
    }
    setImportFileError("");
    if (!organizationId) {
      setImportStatus({
        message: t("noOrganizationId"),
        severity: "error",
      });
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("excel", file);

    try {
      const response = await axios.post(
        `${config.baseURL}/upload/excel?table=${encodeURIComponent(importSection)}&organizationId=${encodeURIComponent(organizationId)}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const { message, errors, success } = response.data;
      const detailedErrorMessage = formatUploadErrors(errors);
      let statusMessage = detailedErrorMessage || message;

      if (errors && errors.length > 0) {
        console.log("Upload errors:", errors);

        if (!detailedErrorMessage && message) {
          statusMessage = `${message}. ${t("errorsOccurred", { count: errors.length })}`;
        }
      }

      setImportStatus({
        message: statusMessage,
        severity: success ? "success" : "warning",
      });

      // Reset state
      setFile(null);
      setImportSection("");
      setImportSectionError("");
      setImportFileError("");
      if (fileInputRef.current) {
        fileInputRef.current.value = null;
      }
    } catch (error) {
      console.error("Upload error:", error);
      const responseData = error.response?.data;
      const detailedErrorMessage =
        formatUploadErrors(responseData?.errors) ||
        formatUploadErrors(responseData?.errorsOccurred);

      setImportStatus({
        message:
          detailedErrorMessage ||
          t("uploadError", {
            error: responseData?.message || error.message,
          }),
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle sample file download
  // const handleDownloadSample = async () => {
  //   setImportStatus({ message: "", severity: "info" });
  //   if (!importSection) {
  //     setImportSectionError("Section is required for sample download.");
  //     return;
  //   }
  //   setImportSectionError("");

  //   try {
  //     const response = await axios.get(
  //       `${config.baseURL}/sample-files/${importSection}.xlsx`,
  //       { responseType: "blob" },
  //     );

  //     const blob = new Blob([response.data], {
  //       type:
  //         response.headers["content-type"] ||
  //         "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  //     });
  //     const downloadUrl = window.URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = downloadUrl;
  //     link.download = `${importSection}.xlsx`;
  //     document.body.appendChild(link);
  //     link.click();
  //     link.remove();
  //     window.URL.revokeObjectURL(downloadUrl);
  //   } catch (error) {
  //     console.error("Sample download error:", error);
  //     setImportStatus({
  //       message:
  //         error.response?.status === 404
  //           ? `Sample format for ${importSection} is not available yet.`
  //           : t("uploadError", {
  //               error: error.response?.data?.message || error.message,
  //             }),
  //       severity: "error",
  //     });
  //   }
  // };
  // Handle sample file download
  const handleDownloadSample = async () => {
    setImportStatus({ message: "", severity: "info" });
    if (!importSection) {
      setImportSectionError("Section is required for sample download.");
      return;
    }
    setImportSectionError("");

    if (!organizationId) {
      setImportStatus({
        message: t("noOrganizationId"),
        severity: "error",
      });
      return;
    }

    try {
      // ←←← ADD organizationId here
      const url = `${config.baseURL}/sample-files/${importSection}.xlsx?organizationId=${encodeURIComponent(organizationId)}`;

      const response = await axios.get(url, { responseType: "blob" });

      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `${importSection}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("Sample download error:", error);
      setImportStatus({
        message:
          error.response?.status === 404
            ? `Sample format for ${importSection} is not available yet.`
            : t("uploadError", {
                error: error.response?.data?.message || error.message,
              }),
        severity: "error",
      });
    }
  };
  // Handle export
  const handleExport = async () => {
    if (!exportSection) {
      setSnackbar({
        open: true,
        message: t("selectSection"),
        severity: "warning",
      });
      return;
    }
    if (!organizationId) {
      setSnackbar({
        open: true,
        message: t("noOrganizationId"),
        severity: "error",
      });
      return;
    }

    setIsUploading(true);
    try {
      const response = await axios.get(`${config.baseURL}/excel/download`, {
        params: { organizationId, table: exportSection },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${exportSection}_export.xlsx`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setSnackbar({
        open: true,
        message: t("exportingData", { section: exportSection }),
        severity: "success",
      });
      setExportSection("");
    } catch (error) {
      console.error("Export error:", error);
      setSnackbar({
        open: true,
        message: t("exportError", {
          error: error.response?.data?.message || error.message,
        }),
        severity: "error",
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Handle snackbar close
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        gap: { xs: "15px", sm: "20px", md: "30px" },
        borderRadius: { xs: "8px", sm: "10px" },
        margin: { xs: "10px", sm: "20px 40px", md: "0px 40px" },
        background: "#FFFFFF",
        border: "1px solid #A5DCF3",
        boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
        padding: { xs: "12px", sm: "15px", md: "20px" },
      }}
    >
      {/* Import Section */}
      <Box sx={{ width: "100%", mb: { xs: 2, md: 0 } }}>
        <Box
          sx={{
            height: { xs: "45px", sm: "50px", md: "55px" },
            borderRadius: { xs: "6px", sm: "8px" },
            padding: { xs: "10px", sm: "12px", md: "12px" },
            background: "#647490",
            boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 600,
              fontSize: { xs: "18px", sm: "20px", md: "24px" },
              color: "#FFFFFF",
              textAlign: "left",
            }}
          >
            {t("importData")}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: "12px", sm: "15px", md: "20px" },
            marginTop: { xs: 1.5, sm: 2 },
            padding: { xs: "8px", sm: "10px" },
            alignItems: "flex-start",
            // backgroundColor: "#043b8d",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: "10px", sm: "15px" },
            }}
          >
            <Box
              sx={{ minWidth: "120px", mb: { xs: "8px", sm: "10px", md: 0 } }}
            >
              <InputLabel
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: { xs: "16px", sm: "18px", md: "20px" },
                  lineHeight: "100%",
                  color: "#1F242E",
                  mb: { xs: 0.5, sm: 1 },
                }}
              >
                {t("section")}
              </InputLabel>
              <FormControl minWidth={120}>
                <MuiSelect
                  value={importSection}
                  onChange={handleImportSectionChange}
                  displayEmpty
                  sx={{
                    height: { xs: "45px", sm: "50px", md: "55px" },
                    borderRadius: { xs: "12px", sm: "16px" },
                    padding: { xs: "12px 20px", sm: "16px 24px" },
                    border: importSectionError
                      ? "2px solid #D32F2F"
                      : "2px solid #183059",
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "&.Mui-focused": {
                      border: importSectionError
                        ? "2px solid #D32F2F"
                        : "2px solid #183059",
                      "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    },
                  }}
                >
                  <MenuItem value="">{t("Select import Module")}</MenuItem>
                  {importSections.map((sec) => (
                    <MenuItem key={sec.value} value={sec.value}>
                      {sec.label}
                    </MenuItem>
                  ))}
                </MuiSelect>
              </FormControl>
              {importSectionError && (
                <Typography
                  sx={{
                    mt: 0.75,
                    color: "#D32F2F",
                    fontFamily: "Roboto",
                    fontWeight: 400,
                    fontSize: { xs: "12px", sm: "13px", md: "14px" },
                    lineHeight: 1.4,
                  }}
                >
                  {importSectionError}
                </Typography>
              )}
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "start",
                marginLeft: { xs: 0, sm: "10px" },
              }}
            >
              <Button
                onClick={handleDownloadSample}
                disabled={isUploading}
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: { xs: "14px", sm: "16px" },
                  color: "#20A8E0",
                  textTransform: "none",
                  display: "flex",
                  marginLeft: { xs: 0, sm: "10px" },
                  height: { xs: "45px", sm: "50px", md: "55px" },
                  padding: "12px 20px",
                  gap: "10px",
                  "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
                }}
              >
                <img
                  src={doenloadsam_icon}
                  alt="Download Sample"
                  style={{ width: "24px", height: "24px" }}
                />
                {t("downloadSampleFormat")}
              </Button>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: { xs: "10px", sm: "15px", md: "20px" },
            }}
          >
            {file && (
              <Box
                sx={{
                  minHeight: { xs: "45px", sm: "50px", md: "55px" },
                  width: { xs: "100%", md: "240px" },
                  display: "flex",
                  alignItems: "center",
                  px: { xs: 2, sm: 2.5 },
                  borderRadius: { xs: "6px", sm: "8px" },
                  border: "1px solid #B7C0CD",
                  backgroundColor: "#F7F9FC",
                  color: "#183059",
                  fontFamily: "Roboto",
                  fontWeight: 500,
                  fontSize: { xs: "13px", sm: "14px", md: "15px" },
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
                title={file.name}
              >
                {file.name}
              </Box>
            )}
            {importFileError && (
              <Box
                sx={{
                  width: { xs: "100%", md: "240px" },
                  color: "#D32F2F",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: { xs: "12px", sm: "13px", md: "14px" },
                  lineHeight: 1.4,
                }}
              >
                {importFileError}
              </Box>
            )}
            <Button
              variant="outlined"
              component="label"
              disabled={isUploading}
              sx={{
                height: { xs: "45px", sm: "50px", md: "55px" },
                width: { xs: "100%", md: "208px" },
                borderRadius: { xs: "6px", sm: "8px" },
                padding: { xs: "12px 20px", sm: "16px 24px" },
                border: "2px solid #20A8E0",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px", md: "18px" },
                color: "#20A8E0",
                textTransform: "none",
                "&:hover": { borderWidth: "2px", borderColor: "#20A8E0" },
                "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
              }}
            >
              <InsertLinkIcon sx={{ transform: "rotate(-90deg)", mr: 1 }} />
              {t("attachFile")}
              <input
                type="file"
                accept=".xlsx"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Button>
            <Button
              onClick={handleUpload}
              disabled={isUploading && !importSection}
              sx={{
                height: { xs: "45px", sm: "50px", md: "55px" },
                width: { xs: "100%", sm: "auto" },
                borderRadius: { xs: "6px", sm: "8px" },
                padding: { xs: "12px 20px", sm: "16px 24px" },
                background: "#F9E719",
                border: "1px solid #414958",
                fontFamily: "Roboto",
                fontWeight: 500,
                fontSize: { xs: "14px", sm: "16px", md: "18px" },
                color: "#1F242E",
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
              }}
            >
              {isUploading ? (
                <CircularProgress size={24} />
              ) : (
                <img
                  src={upload_icon}
                  alt="Upload"
                  style={{ width: "24px", height: "24px" }}
                />
              )}
              {t("upload")}
            </Button>
          </Box>
        </Box>
        {importStatus.message && (
          <Typography
            sx={{
              mt: 1,
              px: { xs: "8px", sm: "10px" },
              color:
                importStatus.severity === "success"
                  ? "#2E7D32"
                  : importStatus.severity === "warning"
                    ? "#ED6C02"
                    : "#D32F2F",
              fontFamily: "Roboto",
              fontWeight: 400,
              fontSize: { xs: "12px", sm: "13px", md: "14px" },
              lineHeight: 1.5,
            }}
          >
            {importStatus.message}
          </Typography>
        )}

        {/* <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: "10px", sm: "15px", md: "20px" },
            marginTop: { xs: "15px", sm: "20px" },
            marginLeft: { xs: "0px", sm: "10px" },
          }}
        >


        </Box> */}
      </Box>

      {/* Export Section */}
      {/* <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            height: { xs: "45px", sm: "50px", md: "55px" },
            borderRadius: { xs: "6px", sm: "8px" },
            padding: { xs: "10px", sm: "12px", md: "12px" },
            background: "#647490",
            boxShadow: "-2px 2px 5px 0px rgba(0,0,0,0.25)",
          }}
        >
          <Typography
            sx={{
              fontFamily: "Roboto",
              fontWeight: 600,
              fontSize: { xs: "18px", sm: "20px", md: "24px" },
              color: "#FFFFFF",
              textAlign: "left",
            }}
          >
            {t("exportData")}
          </Typography>
        </Box>

        <Box sx={{ width: { xs: "100%", md: "40%" }, mt: { xs: 1.5, sm: 2 }, padding: { xs: "8px", sm: "10px" } }}>
          <InputLabel
            sx={{
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "16px", sm: "18px", md: "20px" },
              lineHeight: "100%",
              color: "#1F242E",
              mb: { xs: 0.5, sm: 1 },
            }}
          >
            {t("section")}
          </InputLabel>
          <FormControl fullWidth>
            <MuiSelect
              value={exportSection}
              onChange={handleExportSectionChange}
              displayEmpty
              sx={{
                height: { xs: "45px", sm: "50px", md: "55px" },
                borderRadius: { xs: "12px", sm: "16px" },
                padding: { xs: "12px 20px", sm: "16px 24px" },
                border: "2px solid #183059",
                "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                "&.Mui-focused": {
                  border: "2px solid #183059",
                  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                },
              }}
            >
              <MenuItem value="">{t("Select export Module")}</MenuItem>
              {exportSections.map((sec) => (
                <MenuItem key={sec.value} value={sec.value}>
                  {sec.label}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>

          <Button
            onClick={handleExport}
            disabled={isUploading}
            sx={{
              height: { xs: "45px", sm: "50px", md: "55px" },
              width: { xs: "100%", sm: "auto" },
              borderRadius: { xs: "6px", sm: "8px" },
              padding: { xs: "12px 20px", sm: "16px 24px" },
              background: "#F9E719",
              border: "1px solid #414958",
              fontFamily: "Roboto",
              fontWeight: 500,
              fontSize: { xs: "14px", sm: "16px", md: "18px" },
              color: "#1F242E",
              textTransform: "none",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              mt: { xs: 2, sm: 3 },
              "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
            }}
          >
            {isUploading ? (
              <CircularProgress size={24} />
            ) : (
              <img src={download_icon} alt="Download" style={{ width: "24px", height: "24px" }} />
            )}
            {t("download")}
          </Button>
        </Box>
      </Box> */}

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
